/**
 * ToastContainer — portal-rendered toast stack for high/urgent priority notifications.
 */

import { createPortal } from 'react-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotifications } from '@/hooks/useNotifications';
import { ToastItem } from './ToastItem';
import type { NotificationRecord } from '@/types/notifications';

const MAX_VISIBLE = 3;
const MAX_QUEUE = 10;

export function ToastContainer() {
  const { newNotifications, clearNewNotifications, markRead } = useNotifications();
  const [visibleToasts, setVisibleToasts] = useState<NotificationRecord[]>([]);
  const queueRef = useRef<NotificationRecord[]>([]);
  const processedIdsRef = useRef<Set<string>>(new Set());

  // Declared before the effect below: the effect lists fillVisible in its
  // dependency array, which is evaluated during render, so a `const` declared
  // after the useEffect call would be in the temporal dead zone and throw.
  const fillVisible = useCallback(() => {
    setVisibleToasts((prev) => {
      const slotsAvailable = MAX_VISIBLE - prev.length;
      if (slotsAvailable <= 0 || queueRef.current.length === 0) return prev;
      const toShow = queueRef.current.splice(0, slotsAvailable);
      return [...prev, ...toShow];
    });
  }, []);

  // Process new high/urgent notifications into the queue
  useEffect(() => {
    const highPriority = newNotifications.filter(
      (n) =>
        (n.priority === 'high' || n.priority === 'urgent') &&
        !processedIdsRef.current.has(n.notification_id)
    );

    if (highPriority.length === 0) return;

    highPriority.forEach((n) => processedIdsRef.current.add(n.notification_id));
    clearNewNotifications();

    // Add to queue (cap at MAX_QUEUE)
    queueRef.current = [...queueRef.current, ...highPriority].slice(-MAX_QUEUE);

    // Fill visible slots
    fillVisible();
  }, [newNotifications, clearNewNotifications, fillVisible]);

  const handleDismiss = useCallback(
    (id: string) => {
      setVisibleToasts((prev) => prev.filter((t) => t.notification_id !== id));
      // Fill from queue after dismiss
      setTimeout(fillVisible, 250);
    },
    [fillVisible]
  );

  const handleClick = useCallback(
    async (notification: NotificationRecord) => {
      if (!notification.is_read) {
        try {
          await markRead(notification.notification_id);
        } catch {
          // best effort
        }
      }
      handleDismiss(notification.notification_id);
    },
    [markRead, handleDismiss]
  );

  if (visibleToasts.length === 0) return null;

  return createPortal(
    <div
      className="fixed bottom-4 right-4 z-[9999] flex flex-col-reverse gap-2 pointer-events-none"
      aria-label="Notifications"
    >
      {visibleToasts.map((toast) => (
        <ToastItem
          key={toast.notification_id}
          notification={toast}
          onDismiss={handleDismiss}
          onClick={handleClick}
        />
      ))}
    </div>,
    document.body
  );
}
